import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Upload, FileText, Sparkles, CheckCircle2, AlertTriangle,
  Lightbulb, Target, BookOpen, GraduationCap, Star, ChevronRight,
  ChevronDown, Clock, History, Crown, Lock, RefreshCw, Download,
  Building2, Globe, School, LogOut
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const UNIVERSITY_TYPES = [
  { id: 'uk_general', label: 'UK Universities', icon: Building2, color: 'blue' },
  { id: 'oxbridge', label: 'Oxford & Cambridge', icon: School, color: 'purple' },
  { id: 'us_common_app', label: 'US Universities', icon: Globe, color: 'green' },
  { id: 'all', label: 'All (Comprehensive)', icon: GraduationCap, color: 'amber' }
];

const SCORE_COLORS = {
  high: 'text-green-400',
  medium: 'text-amber-400',
  low: 'text-red-400'
};

const getScoreColor = (score, max = 10) => {
  const percentage = (score / max) * 100;
  if (percentage >= 70) return SCORE_COLORS.high;
  if (percentage >= 50) return SCORE_COLORS.medium;
  return SCORE_COLORS.low;
};

const ScoreCircle = ({ score, maxScore = 10, label, size = 'md' }) => {
  const percentage = (score / maxScore) * 100;
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  const sizes = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };
  
  return (
    <div className={`relative ${sizes[size]} flex items-center justify-center`}>
      <svg className="transform -rotate-90 w-full h-full">
        <circle
          cx="50%"
          cy="50%"
          r="40%"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-gray-700"
        />
        <circle
          cx="50%"
          cy="50%"
          r="40%"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={getScoreColor(score, maxScore)}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-xl font-bold ${getScoreColor(score, maxScore)}`}>
          {score}
        </span>
        {label && <span className="text-xs text-gray-400">{label}</span>}
      </div>
    </div>
  );
};

const PersonalStatementReview = () => {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(null);
  const [accessReason, setAccessReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [activeTab, setActiveTab] = useState('write');
  const [statementText, setStatementText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [universityType, setUniversityType] = useState('uk_general');
  const [subjectArea, setSubjectArea] = useState('');
  const [reviewResult, setReviewResult] = useState(null);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [tips, setTips] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    checkAccess();
    fetchHistory();
  }, []);

  useEffect(() => {
    if (universityType) {
      fetchTips(universityType);
    }
  }, [universityType]);

  const checkAccess = async () => {
    try {
      const response = await axios.get(`${API}/api/personal-statement/check-access`, {
        headers: { Authorization: token }
      });
      setHasAccess(response.data.has_access);
      setAccessReason(response.data.reason || '');
    } catch (error) {
      setHasAccess(false);
      setAccessReason('Unable to verify access');
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/api/personal-statement/history`, {
        headers: { Authorization: token }
      });
      setReviewHistory(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchTips = async (type) => {
    try {
      const response = await axios.get(`${API}/api/personal-statement/tips/${type}`);
      setTips(response.data);
    } catch (error) {
      console.error('Error fetching tips:', error);
    }
  };

  const handleTextReview = async () => {
    if (!statementText || statementText.length < 100) {
      toast.error('Please enter at least 100 characters');
      return;
    }

    setReviewing(true);
    try {
      const response = await axios.post(
        `${API}/api/personal-statement/review/text`,
        {
          statement_text: statementText,
          target_university_type: universityType,
          subject_area: subjectArea || null
        },
        { headers: { Authorization: token } }
      );
      
      setReviewResult(response.data.review);
      toast.success('Review completed!');
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Review failed');
    }
    setReviewing(false);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a PDF file');
      return;
    }

    setReviewing(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('target_university_type', universityType);
    formData.append('subject_area', subjectArea);
    formData.append('authorization', token);

    try {
      const response = await axios.post(
        `${API}/api/personal-statement/review/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setReviewResult(response.data.review);
      toast.success('Review completed!');
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Review failed');
    }
    setReviewing(false);
  };

  const loadPreviousReview = async (reviewId) => {
    try {
      const response = await axios.get(
        `${API}/api/personal-statement/review/${reviewId}`,
        { headers: { Authorization: token } }
      );
      setReviewResult(response.data.review_data);
    } catch (error) {
      toast.error('Failed to load review');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Access denied screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-violet-500/20 flex items-center justify-center">
                <Lock className="w-10 h-10 text-violet-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">
                Premium Feature
              </h1>
              <p className="text-gray-300 mb-6">
                {accessReason || 'Personal Statement Review is available for A-Level students with premium access.'}
              </p>
              <div className="space-y-3">
                <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30 px-4 py-2">
                  <Crown className="w-4 h-4 mr-2" />
                  A-Level + Premium Required
                </Badge>
              </div>
              <Button
                className="mt-6 bg-gradient-to-r from-violet-600 to-purple-600"
                onClick={() => navigate('/subscription')}
              >
                Upgrade to Premium
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-violet-400" />
                Personal Statement Review
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                AI-powered feedback for your university application
              </p>
            </div>
          </div>
          <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">
            <Crown className="w-4 h-4 mr-1" /> Premium
          </Badge>
          <Button
            variant="ghost"
            onClick={() => {
              localStorage.clear();
              window.location.replace('/');
            }}
            className="text-gray-400 hover:text-white"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>

        {!reviewResult ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Input Area */}
            <div className="lg:col-span-2 space-y-6">
              {/* University Type Selection */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-violet-400" />
                    Target Universities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {UNIVERSITY_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = universityType === type.id;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setUniversityType(type.id)}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            isSelected
                              ? `border-${type.color}-500 bg-${type.color}-500/20`
                              : 'border-gray-700 hover:border-gray-600'
                          }`}
                          data-testid={`university-type-${type.id}`}
                        >
                          <Icon className={`w-6 h-6 mx-auto mb-2 ${
                            isSelected ? `text-${type.color}-400` : 'text-gray-400'
                          }`} />
                          <p className={`text-sm font-medium ${
                            isSelected ? 'text-white' : 'text-gray-400'
                          }`}>
                            {type.label}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="mt-4">
                    <label className="text-sm text-gray-400 block mb-2">
                      Subject Area (optional)
                    </label>
                    <input
                      type="text"
                      value={subjectArea}
                      onChange={(e) => setSubjectArea(e.target.value)}
                      placeholder="e.g., Medicine, Computer Science, Law"
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
                      data-testid="subject-area-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Input Tabs */}
              <Card className="bg-gray-800/50 border-gray-700">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <CardHeader>
                    <TabsList className="bg-gray-700/50">
                      <TabsTrigger value="write" className="data-[state=active]:bg-violet-600">
                        <FileText className="w-4 h-4 mr-2" /> Write/Paste
                      </TabsTrigger>
                      <TabsTrigger value="upload" className="data-[state=active]:bg-violet-600">
                        <Upload className="w-4 h-4 mr-2" /> Upload PDF
                      </TabsTrigger>
                    </TabsList>
                  </CardHeader>
                  <CardContent>
                    <TabsContent value="write" className="mt-0">
                      <Textarea
                        value={statementText}
                        onChange={(e) => setStatementText(e.target.value)}
                        placeholder="Paste or write your personal statement here..."
                        className="min-h-[300px] bg-gray-700/50 border-gray-600 text-white placeholder-gray-500 resize-none"
                        data-testid="statement-textarea"
                      />
                      <div className="flex justify-between items-center mt-3">
                        <div className="text-sm text-gray-400">
                          <span className={statementText.length > 4000 ? 'text-red-400' : ''}>
                            {statementText.length}
                          </span>
                          /4000 characters | {statementText.split(/\s+/).filter(w => w).length} words
                        </div>
                        <Button
                          onClick={handleTextReview}
                          disabled={reviewing || statementText.length < 100}
                          className="bg-gradient-to-r from-violet-600 to-purple-600"
                          data-testid="review-text-button"
                        >
                          {reviewing ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Get AI Review
                            </>
                          )}
                        </Button>
                      </div>
                    </TabsContent>

                    <TabsContent value="upload" className="mt-0">
                      <div 
                        className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center hover:border-violet-500 transition-colors cursor-pointer"
                        onClick={() => document.getElementById('pdf-upload').click()}
                      >
                        <input
                          id="pdf-upload"
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={(e) => setSelectedFile(e.target.files[0])}
                          data-testid="pdf-upload-input"
                        />
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        {selectedFile ? (
                          <div>
                            <p className="text-white font-medium">{selectedFile.name}</p>
                            <p className="text-sm text-gray-400 mt-1">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-gray-300">Click to upload PDF</p>
                            <p className="text-sm text-gray-500 mt-1">Maximum 5MB</p>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end mt-4">
                        <Button
                          onClick={handleFileUpload}
                          disabled={reviewing || !selectedFile}
                          className="bg-gradient-to-r from-violet-600 to-purple-600"
                          data-testid="review-upload-button"
                        >
                          {reviewing ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Get AI Review
                            </>
                          )}
                        </Button>
                      </div>
                    </TabsContent>
                  </CardContent>
                </Tabs>
              </Card>
            </div>

            {/* Sidebar - Tips & History */}
            <div className="space-y-6">
              {/* Tips Card */}
              {tips && (
                <Card className="bg-gray-800/50 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-400" />
                      {tips.title?.split(' ').slice(0, 3).join(' ')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {tips.character_limit && (
                      <div className="p-3 bg-gray-700/30 rounded-lg">
                        <p className="text-sm text-gray-400">Limit</p>
                        <p className="text-white font-medium">{tips.character_limit || tips.word_limit}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Key Points</p>
                      <ul className="space-y-2">
                        {tips.key_points?.slice(0, 4).map((point, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                            <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Review History */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-blue-400" />
                    Review History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reviewHistory.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">
                      No reviews yet
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {reviewHistory.map((review) => (
                        <button
                          key={review.review_id}
                          onClick={() => loadPreviousReview(review.review_id)}
                          className="w-full p-3 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-colors text-left"
                          data-testid={`history-item-${review.review_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-white font-medium">
                              Score: {review.overall_score}/100
                            </span>
                            <Badge className="bg-gray-600 text-gray-300 text-xs">
                              {review.target_type?.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(review.created_at).toLocaleDateString()}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* Review Results */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Back to Input Button */}
            <Button
              variant="outline"
              onClick={() => setReviewResult(null)}
              className="border-gray-600 text-gray-300"
              data-testid="back-to-input"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> New Review
            </Button>

            {/* Overall Score Card */}
            <Card className="bg-gradient-to-br from-violet-900/30 to-purple-900/30 border-violet-500/30">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <ScoreCircle 
                    score={reviewResult.overall_score || 0} 
                    maxScore={100} 
                    size="lg"
                  />
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Overall Score: {reviewResult.overall_score}/100
                    </h2>
                    <p className="text-gray-300">
                      {reviewResult.final_verdict}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Badge className="bg-blue-500/20 text-blue-300">
                        {reviewResult.word_count} words
                      </Badge>
                      <Badge className="bg-purple-500/20 text-purple-300">
                        {reviewResult.character_count} characters
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Scores Grid */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-400" />
                  Detailed Scores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {reviewResult.scores && Object.entries(reviewResult.scores).map(([key, value]) => (
                    <div key={key} className="p-4 bg-gray-700/30 rounded-xl text-center">
                      <ScoreCircle score={value.score || 0} maxScore={10} size="sm" />
                      <p className="text-sm text-gray-300 mt-2 capitalize">
                        {key.replace(/_/g, ' ')}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Strengths */}
            {reviewResult.key_strengths?.length > 0 && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-400" />
                    Key Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {reviewResult.key_strengths.map((strength, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                        <span className="text-gray-300">{strength}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Critical Improvements */}
            {reviewResult.critical_improvements?.length > 0 && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Areas for Improvement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {reviewResult.critical_improvements.map((improvement, i) => (
                    <div key={i} className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
                      <h4 className="font-semibold text-white mb-2">{improvement.area}</h4>
                      <p className="text-sm text-gray-400 mb-2">
                        <span className="text-red-400">Issue:</span> {improvement.current_issue}
                      </p>
                      <p className="text-sm text-gray-300">
                        <span className="text-green-400">Suggestion:</span> {improvement.suggestion}
                      </p>
                      {improvement.example_rewrite && (
                        <div className="mt-3 p-3 bg-gray-800/50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Example rewrite:</p>
                          <p className="text-sm text-gray-300 italic">"{improvement.example_rewrite}"</p>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Paragraph Feedback */}
            {reviewResult.paragraph_feedback?.length > 0 && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader 
                  className="cursor-pointer"
                  onClick={() => toggleSection('paragraphs')}
                >
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-400" />
                      Paragraph-by-Paragraph Feedback
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${expandedSections.paragraphs ? 'rotate-180' : ''}`} />
                  </CardTitle>
                </CardHeader>
                <AnimatePresence>
                  {expandedSections.paragraphs && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                    >
                      <CardContent className="space-y-4">
                        {reviewResult.paragraph_feedback.map((para, i) => (
                          <div key={i} className="p-4 bg-gray-700/30 rounded-xl">
                            <h4 className="font-semibold text-white mb-2">
                              Paragraph {para.paragraph_number}
                            </h4>
                            <p className="text-sm text-gray-400 mb-2">{para.summary}</p>
                            <div className="grid md:grid-cols-2 gap-3 mt-3">
                              <div className="p-2 bg-green-500/10 rounded-lg">
                                <p className="text-xs text-green-400 mb-1">Strength</p>
                                <p className="text-sm text-gray-300">{para.strength}</p>
                              </div>
                              <div className="p-2 bg-amber-500/10 rounded-lg">
                                <p className="text-xs text-amber-400 mb-1">Improvement</p>
                                <p className="text-sm text-gray-300">{para.improvement}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            )}

            {/* Oxbridge Specific */}
            {reviewResult.oxbridge_specific && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader 
                  className="cursor-pointer"
                  onClick={() => toggleSection('oxbridge')}
                >
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <School className="w-5 h-5 text-purple-400" />
                      Oxford & Cambridge Assessment
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${expandedSections.oxbridge ? 'rotate-180' : ''}`} />
                  </CardTitle>
                </CardHeader>
                <AnimatePresence>
                  {expandedSections.oxbridge && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                    >
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex items-center gap-4">
                            <Badge className={reviewResult.oxbridge_specific.suitable_for_oxbridge 
                              ? 'bg-green-500/20 text-green-300' 
                              : 'bg-amber-500/20 text-amber-300'
                            }>
                              {reviewResult.oxbridge_specific.suitable_for_oxbridge 
                                ? 'Suitable for Oxbridge' 
                                : 'Needs Improvement for Oxbridge'}
                            </Badge>
                            <span className="text-gray-400">
                              Intellectual Depth: {reviewResult.oxbridge_specific.intellectual_depth_score}/10
                            </span>
                          </div>
                          <p className="text-gray-300">{reviewResult.oxbridge_specific.super_curricular_evidence}</p>
                          <p className="text-gray-300">{reviewResult.oxbridge_specific.recommendations}</p>
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            )}

            {/* US Specific */}
            {reviewResult.us_specific && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader 
                  className="cursor-pointer"
                  onClick={() => toggleSection('us')}
                >
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-green-400" />
                      US Application Assessment
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${expandedSections.us ? 'rotate-180' : ''}`} />
                  </CardTitle>
                </CardHeader>
                <AnimatePresence>
                  {expandedSections.us && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                    >
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex items-center gap-4">
                            <Badge className={reviewResult.us_specific.suitable_for_us 
                              ? 'bg-green-500/20 text-green-300' 
                              : 'bg-amber-500/20 text-amber-300'
                            }>
                              {reviewResult.us_specific.suitable_for_us 
                                ? 'Suitable for US Apps' 
                                : 'Needs Adaptation for US'}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-gray-700/30 rounded-lg text-center">
                              <p className="text-2xl font-bold text-white">
                                {reviewResult.us_specific.personal_narrative_score}/10
                              </p>
                              <p className="text-sm text-gray-400">Personal Narrative</p>
                            </div>
                            <div className="p-3 bg-gray-700/30 rounded-lg text-center">
                              <p className="text-2xl font-bold text-white">
                                {reviewResult.us_specific.authenticity_score}/10
                              </p>
                              <p className="text-sm text-gray-400">Authenticity</p>
                            </div>
                          </div>
                          <p className="text-gray-300">{reviewResult.us_specific.recommendations}</p>
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            )}

            {/* Improved Suggestions */}
            {(reviewResult.improved_opening || reviewResult.improved_closing) && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-400" />
                    Suggested Improvements
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {reviewResult.improved_opening && (
                    <div className="p-4 bg-violet-500/10 rounded-xl border border-violet-500/20">
                      <h4 className="font-semibold text-violet-300 mb-2">Improved Opening</h4>
                      <p className="text-gray-300 italic">"{reviewResult.improved_opening}"</p>
                    </div>
                  )}
                  {reviewResult.improved_closing && (
                    <div className="p-4 bg-violet-500/10 rounded-xl border border-violet-500/20">
                      <h4 className="font-semibold text-violet-300 mb-2">Improved Closing</h4>
                      <p className="text-gray-300 italic">"{reviewResult.improved_closing}"</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PersonalStatementReview;
